/**
 * Date utility functions for board components
 */

export const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const DEFAULT_TIME_ZONE = "UTC";

/** Return whether Intl recognizes an IANA timezone name. */
export function isValidTimeZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format();
    return true;
  } catch {
    return false;
  }
}

/** Read the browser timezone when available. Intl does not require a permission prompt. */
export function getBrowserTimeZone(): string | null {
  if (typeof window === "undefined") return null;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidTimeZone(timeZone) ? timeZone : null;
}

/**
 * Convert an instant to its calendar date in a timezone, represented at UTC
 * midnight so the app's existing UTC-only date arithmetic stays deterministic.
 */
export function calendarDateInTimeZone(date: Date, timeZone: string): Date {
  const safeTimeZone = isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIME_ZONE;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: safeTimeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return new Date(Date.UTC(value("year"), value("month") - 1, value("day")));
}

/** Monday of the week containing `now` in the user's effective timezone. */
export function currentWeekStartInTimeZone(timeZone: string, now = new Date()): Date {
  return startOfWeek(calendarDateInTimeZone(now, timeZone));
}

/**
 * Parse an ISO date string to a Date object
 */
export function parseISODate(value: string): Date {
  return new Date(value);
}

/**
 * Format a UTC date into a string in the format (e.g., "Dec 1, 2025")
 */
export function formatUTCDateMDY(date: string | Date | null | undefined): string {
  if (!date) return "TBD";
  const formatOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }
  if (typeof date === "string") {
    return parseISODate(date).toLocaleDateString(undefined, formatOptions);
  }
  return date.toLocaleDateString(undefined, formatOptions);
};


/**
 * Format a date as a week label (e.g., "Jan 14")
 */
export function formatUTCDateWeek(date: Date): string {
  if (!date) return "TBD";
  const formatOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }
  if (typeof date === "string") {
    return parseISODate(date).toLocaleDateString(undefined, formatOptions);
  }
  return date.toLocaleDateString(undefined, formatOptions);
}

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MILLISECONDS_PER_DAY);
}

/**
 * Add weeks to a date
 */
export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

/**
 * Get the start of the week (Monday) for a given date
 */
export function startOfWeek(date: Date): Date {
  const result = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  const day = result.getUTCDay();
  const diff = (day + 6) % 7;
  result.setUTCDate(result.getUTCDate() - diff);
  return result;
}

// ============================================================================
// WeekStepper Utilities
// These functions support week-based date stepping and shifting operations.
// ============================================================================

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Convert a date string or Date to an ISO date string (YYYY-MM-DD)
 * Uses UTC components to avoid timezone-related date shifts.
 */
export function toISODateString(date: string | Date): string {
  if (typeof date === "string") {
    return date.slice(0, 10);
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Compute the delta in days between two dates.
 * 
 * @param fromDate - Starting date (ISO string or Date)
 * @param toDate - Ending date (ISO string or Date)
 * @returns Number of days (positive if toDate > fromDate)
 * 
 * @example
 * dateDeltaDays("2025-01-29", "2025-02-05") // 7
 * dateDeltaDays("2025-02-05", "2025-01-29") // -7
 */
export function dateDeltaDays(
  fromDate: string | Date,
  toDate: string | Date
): number {
  const from = typeof fromDate === "string" ? parseISODate(fromDate) : fromDate;
  const to = typeof toDate === "string" ? parseISODate(toDate) : toDate;
  return Math.round((to.getTime() - from.getTime()) / MILLISECONDS_PER_DAY);
}

export type YearFormat = "YY" | "YYYY" | undefined;

/**
 * Format a date for display in week labels.
 * @param date - The date to format
 * @param yearFormat - How to display the year:
 *   - undefined: Show year only if different from current year (2-digit)
 *   - "YY": Always show 2-digit year (e.g., "Jan 29 '25")
 *   - "YYYY": Always show 4-digit year (e.g., "Jan 29 2025")
 */
export function formatWeekDateLabel(date: Date, yearFormat?: YearFormat): string {
  const currentYear = new Date().getFullYear();
  const dateYear = date.getUTCFullYear();
  
  const showYear = yearFormat === "YY" || yearFormat === "YYYY" || dateYear !== currentYear;

  if (showYear && yearFormat !== "YYYY") {
    // Compact numeric format to fit narrow cells: MM/DD/YY
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");
    const yy = String(date.getUTCFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  }

  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
    ...(yearFormat === "YYYY" && { year: "numeric" }),
  };
  return date.toLocaleDateString(undefined, options);
}

/**
 * Get a reference date that falls on the specified weekday.
 * Used as the base for offset calculations in WeekStepper.
 */
/**
 * This week's Monday, as an ISO date. Project date pickers pass this as their
 * floor: work can't be scheduled into a week that has already gone by. It's a
 * project rule, not a property of the week picker, which is why it lives at the
 * call sites rather than in the picker's defaults.
 *
 * "This week" is the browser's local week: startOfWeek reads UTC fields, so
 * passing the raw instant would put Sunday evening in the Americas in next
 * week and early Monday east of UTC in last week.
 */
export function currentWeekStartISO(now = new Date()): string {
  return toISODateString(startOfWeek(todayInBrowserTimeZone(now)));
}

/** Today's calendar date in the browser's timezone, at UTC midnight. */
export function todayInBrowserTimeZone(now = new Date()): Date {
  return calendarDateInTimeZone(now, getBrowserTimeZone() ?? DEFAULT_TIME_ZONE);
}

export function getReferenceDateForWeekday(weekday: Weekday): Date {
  const todayUTC = todayInBrowserTimeZone();
  const currentDay = todayUTC.getUTCDay();
  const diff = weekday - currentDay;
  return addDays(todayUTC, diff);
}

/**
 * Calculate the offset between a date and a reference date in step units.
 * Used by WeekStepper to convert dates to stepper positions.
 */
export function dateToOffset(date: Date, referenceDate: Date, step: number): number {
  const diffMs = date.getTime() - referenceDate.getTime();
  const diffDays = Math.round(diffMs / MILLISECONDS_PER_DAY);
  return Math.round(diffDays / step);
}

/**
 * Convert an offset to a date based on a reference date and step size.
 * Used by WeekStepper to convert stepper positions back to dates.
 */
export function offsetToDate(offset: number, referenceDate: Date, step: number): Date {
  return addDays(referenceDate, offset * step);
}

/**
 * Snap a date to the next matching weekday (always forward/ceil).
 * Used to ensure dates align with the configured weekday in WeekStepper.
 */
export function snapToNearestWeekday(date: Date, weekday: Weekday): Date {
  const currentDay = date.getUTCDay();
  if (currentDay === weekday) return date;

  // Days until next occurrence of weekday (always forward)
  const diff = (weekday - currentDay + 7) % 7;
  return addDays(date, diff);
}

/**
 * Parse user input like "Mar 30" or "Mar 30, 26" into a Date.
 * Supports formats:
 *   - "Mar 30"
 *   - "Mar 30, 26"
 *   - "Mar 30 '26"
 *   - "March 30, 2026"
 * Returns null if parsing fails.
 */
export function parseUserDateInput(input: string): Date | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const currentYear = new Date().getFullYear();
  
  // Match: "Mon DD" or "Month DD" with optional year
  const match = trimmed.match(
    /^([A-Za-z]+)\s+(\d{1,2})(?:[,\s]+(?:'?)(\d{2,4}))?$/
  );
  
  if (!match) return null;
  
  const [, monthStr, dayStr, yearStr] = match;
  const day = parseInt(dayStr!, 10);
  
  // Parse month name
  const monthNames = [
    "jan", "feb", "mar", "apr", "may", "jun",
    "jul", "aug", "sep", "oct", "nov", "dec"
  ];
  const monthIndex = monthNames.findIndex(m => 
    monthStr!.toLowerCase().startsWith(m)
  );
  if (monthIndex === -1) return null;
  
  // Parse year
  let year = currentYear;
  if (yearStr) {
    year = parseInt(yearStr, 10);
    if (year < 100) {
      year += 2000; // "26" -> 2026
    }
  }
  
  // Validate day
  if (day < 1 || day > 31) return null;
  
  const date = new Date(Date.UTC(year, monthIndex, day));
  
  // Verify the date is valid (e.g., Feb 30 would roll over)
  if (date.getUTCMonth() !== monthIndex || date.getUTCDate() !== day) {
    return null;
  }
  
  return date;
}

/**
 * Find the date option closest to a target date.
 * @param dates - Array of date options with offset and date properties
 * @param targetDate - The target date to find the closest match to
 * @returns The closest date option, or undefined if dates array is empty
 */
/**
 * Compute the project end date (Monday of the last week) as an ISO date string.
 * end_date = start_date + (num_weeks - 1) * 7 days
 */
export function computeProjectEndDate(startDate: string, numWeeks: number): string {
  const start = parseISODate(startDate);
  const endDate = addDays(start, (numWeeks - 1) * 7);
  return toISODateString(endDate);
}

/**
 * Compute the number of weeks from a start date and end date.
 * Inverse of computeProjectEndDate: num_weeks = (end - start) / 7 + 1
 */
export function computeNumWeeks(startDate: string, endDate: string): number {
  const days = dateDeltaDays(startDate, endDate);
  return Math.round(days / 7) + 1;
}

export function findClosestDateOption<T extends { date: Date }>(
  dates: T[],
  targetDate: Date
): T | undefined {
  if (dates.length === 0) return undefined;
  
  const targetTime = targetDate.getTime();
  let closest = dates[0];
  let minDiff = Infinity;
  
  for (const option of dates) {
    const diff = Math.abs(option.date.getTime() - targetTime);
    if (diff < minDiff) {
      minDiff = diff;
      closest = option;
    }
  }
  
  return closest;
}
